import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoIosSearch } from "react-icons/io";
import {
  FiBriefcase,
  FiFileText,
  FiHome,
  FiShield,
  FiUsers,
} from "react-icons/fi";
import axiosInstance from "../../axiosinstance";
import "../../Tables/Table.css";
import "./Deeds.css";

const getDeedIcon = (deedName = "") => {
  const normalizedName = deedName.toLowerCase();

  if (
    normalizedName.includes("lease") ||
    normalizedName.includes("sale") ||
    normalizedName.includes("rent") ||
    normalizedName.includes("property") ||
    normalizedName.includes("mortgage")
  ) {
    return FiHome;
  }

  if (
    normalizedName.includes("partnership") ||
    normalizedName.includes("business") ||
    normalizedName.includes("agreement") ||
    normalizedName.includes("employment")
  ) {
    return FiBriefcase;
  }

  if (
    normalizedName.includes("family") ||
    normalizedName.includes("marriage") ||
    normalizedName.includes("settlement")
  ) {
    return FiUsers;
  }

  if (
    normalizedName.includes("affidavit") ||
    normalizedName.includes("power") ||
    normalizedName.includes("will") ||
    normalizedName.includes("declaration")
  ) {
    return FiShield;
  }

  return FiFileText;
};

const DeedPreviewModal = ({
  isOpen,
  onClose,
  deedName,
  loading,
  error,
  onRetry,
  previewText,
  rawPreview,
  onStart,
  startLoading,
  startError,
}) => {
  if (!isOpen) return null;

  const previewItems = Array.isArray(rawPreview?.structure)
    ? rawPreview.structure
    : Array.isArray(rawPreview?.clauses)
      ? rawPreview.clauses
      : Array.isArray(rawPreview?.data?.structure)
        ? rawPreview.data.structure
        : Array.isArray(rawPreview?.data?.clauses)
          ? rawPreview.data.clauses
          : [];
  const sortedItems = previewItems
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const aSeq = Number.isFinite(a.item?.sequence)
        ? a.item.sequence
        : Number.MAX_SAFE_INTEGER;
      const bSeq = Number.isFinite(b.item?.sequence)
        ? b.item.sequence
        : Number.MAX_SAFE_INTEGER;
      if (aSeq !== bSeq) return aSeq - bSeq;
      return a.index - b.index;
    })
    .map((entry) => entry.item);
  const deedTitle = rawPreview?.deed?.name || deedName || "Deed Preview";

  const formatClauseText = (text) => {
    if (!text) return [];
    const withBlanks = text.replace(/{{[^}]+}}/g, "____________________");
    return withBlanks.split("\n").filter((line) => line.trim().length > 0);
  };

  return (
    <div className="deeds-modal-overlay" onClick={onClose}>
      <div className="deeds-modal" onClick={(e) => e.stopPropagation()}>
        <div className="deeds-modal-header">
          <div>
            <div className="deeds-modal-title">Preview</div>
            <div className="deeds-modal-subtitle">
              Review the deed template before filling details.
            </div>
          </div>
          <button
            className="deeds-modal-close"
            type="button"
            onClick={onClose}
            aria-label="Close preview"
          >
            X
          </button>
        </div>

        <div className="deeds-modal-body">
          {loading && <div className="deeds-muted">Loading preview...</div>}

          {!loading && error && (
            <div className="deeds-error-inline">
              <span>{error}</span>
              <button className="btn btn-primary" onClick={onRetry}>
                Retry
              </button>
            </div>
          )}

          {!loading && !error && (
            <div className="deeds-modal-document">
              {deedTitle && (
                <div className="deeds-document-title">{deedTitle}</div>
              )}
              {previewText && (
                <div className="deeds-preview-text">{previewText}</div>
              )}

              {!previewText && sortedItems.length > 0 && (
                <div className="deeds-template">
                  {sortedItems.map((item, index) => {
                    const isSection = item?.type === "section";
                    const title = item?.title;
                    const normalizedTitle = (title || "").trim().toLowerCase();
                    const normalizedDeedTitle = (deedTitle || "").trim().toLowerCase();
                    const text = item?.text || "";
                    const lines = formatClauseText(text);

                    if (normalizedTitle && normalizedTitle === normalizedDeedTitle) {
                      return null;
                    }

                    if (isSection) {
                      return (
                        <div className="deeds-section" key={`section-${item?.section_id || index}`}>
                          <div className="deeds-section-title">{title}</div>
                        </div>
                      );
                    }

                    return (
                      <div className="deeds-clause" key={`clause-${item?.clause_number || "x"}-${index}`}>
                        {(title || item?.clause_number) && (
                          <div className="deeds-clause-title">
                            {item?.clause_number?.toString().trim()
                              ? `${item.clause_number}. `
                              : ""}
                            {title || ""}
                          </div>
                        )}
                        <div className="deeds-clause-text">
                          {lines.map((line, idx) => (
                            <p key={idx}>{line}</p>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {!previewText && sortedItems.length === 0 && rawPreview && (
                <pre className="deeds-preview-json">
                  {JSON.stringify(rawPreview, null, 2)}
                </pre>
              )}

              {!previewText && sortedItems.length === 0 && !rawPreview && (
                <div className="deeds-muted">No preview content available.</div>
              )}
            </div>
          )}
        </div>

        <div className="deeds-modal-footer">
          {startError && <div className="deeds-modal-error">{startError}</div>}
          <button
            className="btn btn-primary"
            type="button"
            onClick={onStart}
            disabled={startLoading || loading}
          >
            {startLoading ? "Starting..." : "Fill out the template"}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeedsPage = ({ isSidebarExpanded }) => {
  const [deedTypes, setDeedTypes] = useState([]);
  const [deedsLoading, setDeedsLoading] = useState(true);
  const [deedsError, setDeedsError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [selectedDeed, setSelectedDeed] = useState(null);
  const [selectedDeedInitial, setSelectedDeedInitial] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [startLoading, setStartLoading] = useState(false);
  const [startError, setStartError] = useState("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [deedInitialMap, setDeedInitialMap] = useState({});
  const navigate = useNavigate();

  const fetchDeedTypes = async () => {
    setDeedsLoading(true);
    setDeedsError("");
    try {
      const res = await axiosInstance.get("/api/deed_types");
      const list = Array.isArray(res.data?.deed_types)
        ? res.data.deed_types
        : Array.isArray(res.data)
          ? res.data
          : [];
      setDeedTypes(list);
    } catch (error) {
      console.error("Error fetching deed types:", error);
      setDeedTypes([]);
      setDeedsError("Failed to load deed types.");
    } finally {
      setDeedsLoading(false);
    }
  };

  useEffect(() => {
    fetchDeedTypes();
  }, []);

  const filteredDeeds = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return deedTypes;
    return deedTypes.filter((deed) =>
      (deed?.name || "").toLowerCase().includes(query)
    );
  }, [deedTypes, searchQuery]);

  const openPreview = async (deed) => {
    if (!deed?.id) return;
    setSelectedDeed(deed);
    setIsPreviewOpen(true);
    setPreviewError("");
    setStartError("");

    if (deedInitialMap[deed.id]) {
      setSelectedDeedInitial(deedInitialMap[deed.id]);
      setPreviewLoading(false);
      return;
    }

    setSelectedDeedInitial(null);
    setPreviewLoading(true);

    try {
      const res = await axiosInstance.get(`/api/${deed.id}/initial`);
      console.log("Deed initial response:", res.data);
      setSelectedDeedInitial(res.data);
      setDeedInitialMap((prev) => ({ ...prev, [deed.id]: res.data }));
    } catch (error) {
      console.error("Error fetching deed initial:", error);
      setPreviewError("Failed to load template preview.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleRetryPreview = () => {
    if (selectedDeed) openPreview(selectedDeed);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setStartError("");
    setStartLoading(false);
  };

  useEffect(() => {
    if (!isPreviewOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsPreviewOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isPreviewOpen]);

  useEffect(() => {
    if (!isPreviewOpen) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isPreviewOpen]);

  const previewText = useMemo(() => {
    if (!selectedDeedInitial) return "";
    if (typeof selectedDeedInitial === "string") return selectedDeedInitial;

    const candidates = [
      selectedDeedInitial?.preview_html,
      selectedDeedInitial?.html,
      selectedDeedInitial?.preview,
      selectedDeedInitial?.content,
      selectedDeedInitial?.template,
      selectedDeedInitial?.text,
      selectedDeedInitial?.body,
    ];

    const found = candidates.find(
      (value) => typeof value === "string" && value.trim().length > 0
    );
    return found || "";
  }, [selectedDeedInitial]);

  const handleStartDeed = async () => {
    if (!selectedDeed?.id) {
      setStartError("Missing deed type. Please close and try again.");
      return;
    }

    setStartLoading(true);
    setStartError("");

    try {
      const res = await axiosInstance.post(`/api/start_deed/${selectedDeed.id}`);
      const instanceId = res?.data?.instance_id;

      if (!instanceId) {
        throw new Error("Missing instance id");
      }

      setIsPreviewOpen(false);
      navigate(`/deeds/fill/${instanceId}`, {
        state: {
          instanceId,
          deed_type_id: selectedDeed.id,
          initialResponse: selectedDeedInitial,
        },
      });
    } catch (error) {
      console.error("Error starting deed:", error);
      setStartError("Failed to start deed. Please try again.");
    } finally {
      setStartLoading(false);
    }
  };

  return (
    <div
      className={`deeds-container deeds-index-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="deeds-main oa-attached-panel">
        {/* <div className="deeds-header">
          <h1 className="deeds-title">Deeds</h1>
          <div className="deeds-search">
            <input
              type="text"
              placeholder="Search deeds..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search deeds"
            />
          </div>
        </div> */}

        <div className="table-header oa-attached-panel__header-cap">
  <div className="table-header-content">

    {/* LEFT SIDE */}
    <div className="table-left-side">
      <h2 className="jost-bold">Deeds</h2>
    </div>

    {/* RIGHT SIDE */}
    <div className="table-right-side">
      <div className="search-bar-container">
        <input
          type="text"
          placeholder="Search deeds..."
          className="search-bar"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search deeds"
        />
        <IoIosSearch className="search-bar-icon" />
      </div>
    </div>

  </div>
</div>

        {deedsError && (
          <div className="deeds-error">
            <span>{deedsError}</span>
            <button className="btn btn-primary" onClick={fetchDeedTypes}>
              Retry
            </button>
          </div>
        )}

        <div className="deeds-layout oa-attached-panel__body">
          <div className="deeds-list">
            {deedsLoading && <div className="deeds-muted">Loading...</div>}

            {!deedsLoading && !filteredDeeds.length && (
              <div className="deeds-muted">No deeds found.</div>
            )}

            <div className="deeds-grid">
              {filteredDeeds.map((deed) => {
                const isSelected = selectedDeed?.id === deed?.id;
                const DeedIcon = getDeedIcon(deed?.name || "");
                return (
                  <div
                    key={deed?.id || deed?.name}
                    className={`deed-card ${isSelected ? "selected" : ""}`}
                  >
                    <div className="deed-card-header">
                      <div className="deed-card-icon" aria-hidden="true">
                        <DeedIcon />
                      </div>
                      <div className="deed-card-title">
                        {deed?.name || "Untitled deed"}
                      </div>
                    </div>
                    <div className="deed-card-body">
                      <div className="deed-card-desc">
                        Create and fill this deed template
                      </div>
                      <button
                        className="deed-card-cta"
                        type="button"
                        onClick={() => openPreview(deed)}
                      >
                        Preview
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <DeedPreviewModal
        isOpen={isPreviewOpen}
        onClose={handleClosePreview}
        deedName={selectedDeed?.name}
        loading={previewLoading}
        error={previewError}
        onRetry={handleRetryPreview}
        previewText={previewText}
        rawPreview={selectedDeedInitial}
        onStart={handleStartDeed}
        startLoading={startLoading}
        startError={startError}
      />
    </div>
  );
};

export default DeedsPage;
